import { useEffect, useState } from 'react';
import { Eye, EyeOff, ShieldOff } from 'lucide-react';
import api from '../../../utils/api';
import { PartnerEndpoints } from '../../../utils/endpoints';
import { apiErrorMessage } from '../../../utils/apiError';
import { formatUtc } from '../../../utils/datetime';
import { Alert, Button, CopyButton, EmptyState, IconButton } from '../../ui';
import ConfirmActionModal from '../modals/ConfirmActionModal';

const UNSIGNED_WARNING =
  'Your webhook deliveries are not signed. Until you generate a signing secret they carry only the deprecated hash header, which cannot prove a delivery came from CashAfrica. Generating one is safe: it adds a signature to future deliveries without changing anything your endpoint already receives.';

const ROTATION_HAZARD =
  'For a grace window after this, we sign every delivery with both the old and the new secret, so an endpoint that accepts either one keeps working. Roll the new secret out to your endpoint before that window closes — and do not rotate again until you have, because a second rotation inside the window strands any endpoint still on the original secret.';

const maskHex = (value) => `${String(value).slice(0, 8)}••••••••`;

const WebhookSecret = ({ secret, canRotate, fetchDetails }) => {
  const [revealed, setRevealed] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState('');

  // fetchDetails swallows its own errors, so the rotate response is what keeps the new secret
  // on screen when the follow-up GET /details fails — copying the old one would strand the receiver.
  const shownSecret = outcome?.secret || secret;

  // Generating API credentials rotates this secret too, so a different value arriving from
  // /details supersedes the response being held and its now-void grace window.
  useEffect(() => {
    setOutcome((current) => (current && secret && secret !== current.secret ? null : current));
  }, [secret]);
  const hasSecret = typeof shownSecret === 'string' && shownSecret.length > 0;

  const submit = async () => {
    const rotated = hasSecret;
    const response = await api.post(PartnerEndpoints.rotateWebhookSecret, {});
    const rotation = response.data?.data ?? {};
    setOutcome({
      rotated,
      secret: rotation.webhook_secret ?? '',
      graceUntil: rotation.previous_secret_valid_until ?? ''
    });
    setRevealed(false);
    await fetchDetails?.();
  };

  const generate = async () => {
    setError('');
    setGenerating(true);
    try {
      await submit();
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not generate the signing secret. Please try again.'));
    }
    setGenerating(false);
  };

  const outcomeMessage = () => {
    if (!outcome.rotated) return 'Signing secret generated. Deliveries are signed from now on.';
    if (!outcome.graceUntil) return 'Signing secret rotated. Update your endpoint with the new secret.';
    return (
      <>
        Signing secret rotated. Both the old and the new secret are accepted until{' '}
        <strong>{`${formatUtc(outcome.graceUntil)} UTC`}</strong>. Update your endpoint before then — this deadline is
        not stored anywhere and will not be shown again after you reload.
      </>
    );
  };

  return (
    <div className="space-y-4">
      {outcome && (
        // previous_secret_valid_until comes back only from the rotate response and never from
        // /details, so this alert is never auto-dismissed on a timer.
        <Alert variant="success" onDismiss={() => setOutcome(null)}>
          {outcomeMessage()}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}

      {hasSecret ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="form-label mb-1">Signing secret</p>
              <div className="flex items-center gap-2">
                <code className="break-all font-medium text-theme-headings dark:text-themedark-headings">
                  {revealed ? shownSecret : maskHex(shownSecret)}
                </code>
                <IconButton
                  icon={revealed ? EyeOff : Eye}
                  label={revealed ? 'Hide signing secret' : 'Reveal signing secret'}
                  onClick={() => setRevealed((current) => !current)}
                />
                <CopyButton text={shownSecret} label="Copy secret" />
              </div>
            </div>
            {canRotate && (
              <Button variant="outline-danger" size="sm" onClick={() => setConfirming(true)}>
                Rotate signing secret
              </Button>
            )}
          </div>

          <p className="text-sm text-theme-secondarytextcolor dark:text-themedark-secondarytextcolor">
            We sign every webhook delivery with this secret. Treat it like a password: keep it on your server and never
            ship it to a browser or mobile app.
          </p>
        </>
      ) : (
        <>
          <Alert variant="warning">{UNSIGNED_WARNING}</Alert>
          <EmptyState
            icon={<ShieldOff className="w-8 h-8" />}
            title="No signing secret"
            message="Generate one and we will start signing your webhook deliveries."
            action={
              canRotate ? (
                <Button size="sm" loading={generating} onClick={generate}>
                  Generate signing secret
                </Button>
              ) : undefined
            }
          />
        </>
      )}

      <ConfirmActionModal
        isOpen={confirming}
        title="Rotate signing secret"
        message={ROTATION_HAZARD}
        confirmLabel="Rotate secret"
        confirmVariant="danger"
        onClose={() => setConfirming(false)}
        onConfirm={submit}
      />
    </div>
  );
};

export default WebhookSecret;
